import Phaser from 'phaser';
import { PathPoint } from './PathPoint';
import Node from './Node';
import { PathHitArea } from './PathHitArea';

export default class Path {
    private relatedScene: Phaser.Scene;
    public origenNode: Node;
    public destineNode: Node;
    public trace?: Phaser.Curves.Path;
    private additionalPoints: PathPoint[];
    private pathZone?: Phaser.GameObjects.Zone;
    private debugMode = false;
    private hitArea: PathHitArea;
    private pathGraphics?: Phaser.GameObjects.Graphics;

    private static readonly LINE_WIDTH = 4;
    private static readonly NORMAL_COLOR = 0xffffff;
    private static readonly HIGHLIGHT_COLOR = 0xffff00;
    private static readonly ALPHA = 1;
    private static readonly DEPTH = 1;

    constructor(
        scene: Phaser.Scene,
        origenNode: Node,
        destineNode: Node,
        additionalPoints: PathPoint[] = []
    ) {
        this.relatedScene = scene;
        this.origenNode = origenNode;
        this.destineNode = destineNode;
        this.additionalPoints = additionalPoints;
        this.debugMode = this.getDebugMode();
        this.hitArea = new PathHitArea(scene, this.debugMode);
    }

    public create(): void {
        this.buildTrace();
        this.renderTrace();
        if (this.trace) {
            this.pathZone = this.hitArea.create(this.trace);
        }
        this.attachPathReference();
    }

    private buildTrace(): void {
        this.trace = new Phaser.Curves.Path(this.origenNode.x, this.origenNode.y);
        this.additionalPoints.forEach((point: PathPoint) => {
            this.trace?.lineTo(point.x, point.y);
        });
        this.trace.lineTo(this.destineNode.x, this.destineNode.y);
    }

    private renderTrace(): void {
        this.pathGraphics = this.relatedScene.add.graphics();
        this.pathGraphics.lineStyle(Path.LINE_WIDTH, Path.NORMAL_COLOR, Path.ALPHA);
        if (this.trace) {
            const path = this.trace.draw(this.pathGraphics);
            path.setDepth(Path.DEPTH);
        }
    }

    private attachPathReference(): void {
        if (this.pathZone) {
            (this.pathZone as any).pathReference = this;
        }
    }

    private getDebugMode(): boolean {
        const bootScene = (this.relatedScene as any).getBootloaderScene?.();
        return bootScene?.globals?.debugMode || false;
    }

    public destroy(): void {
        this.hitArea.destroy();
        this.trace = undefined;
    }

    public update(): void {
        // Empty for now
    }

    public bringToFront(): void {
        this.hitArea.bringToFront();
    }

    public sendToBack(): void {
        this.hitArea.sendToBack();
    }

    public highlight(): void {
        if (this.pathGraphics && this.trace) {
            this.pathGraphics.clear();
            this.pathGraphics.lineStyle(Path.LINE_WIDTH, Path.HIGHLIGHT_COLOR, Path.ALPHA);
            this.trace.draw(this.pathGraphics);
        }
    }

    public unhighlight(): void {
        if (this.pathGraphics && this.trace) {
            this.pathGraphics.clear();
            this.pathGraphics.lineStyle(Path.LINE_WIDTH, Path.NORMAL_COLOR, Path.ALPHA);
            this.trace.draw(this.pathGraphics);
        }
    }

    public enableHover(): void {
        this.pathZone?.on('pointerover', () => this.highlight());
        this.pathZone?.on('pointerout', () => this.unhighlight());
    }

    public disableHover(): void {
        this.pathZone?.off('pointerover');
        this.pathZone?.off('pointerout');
    }
}